import React from "react";
import { resolveMediaUrl } from "../../../../../lib/utils";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function ObservationReviewCard({
  reviewerRole = "Checker",
  reviewerName = "",
  decision = "REVIEWED",
  remarks = "",
  reviewedAt = null,
  signature = null,
  rejectionPhotos = [],
  onImageClick = null,
  className = "",
}) {
  const normalizedDecision = String(decision || "").trim().toUpperCase();
  const isRejected = normalizedDecision === "REJECTED";
  const isApproved =
    normalizedDecision === "APPROVED" ||
    normalizedDecision === "APPROVED / FORWARDED";

  const containerTheme = isRejected
    ? "border-red-200 bg-red-50/70"
    : isApproved
      ? "border-emerald-200 bg-emerald-50/60"
      : "border-slate-200 bg-slate-50";

  const badgeTheme = isRejected
    ? "bg-red-100 text-red-700"
    : isApproved
      ? "bg-emerald-100 text-emerald-800"
      : "bg-slate-200 text-slate-700";

  const roleTextTheme = isRejected
    ? "text-red-700"
    : isApproved
      ? "text-emerald-800"
      : "text-slate-600";

  const photosList = Array.isArray(rejectionPhotos) ? rejectionPhotos : [];

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition-colors ${containerTheme} ${className}`}
    >
      {/* Header: Reviewer Role, Name, Decision Badge, Timestamp */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/5 pb-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <p className={`text-[11px] font-bold uppercase tracking-wide ${roleTextTheme}`}>
              {reviewerRole}
            </p>
            <p className="mt-0.5 text-sm font-bold text-slate-800">
              {reviewerName || "Reviewer"}
            </p>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {isRejected ? "Rejected At" : "Reviewed At"}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-slate-700">
              {formatDateTime(reviewedAt)}
            </p>
          </div>
        </div>

        <span className={`rounded-full px-3 py-1 text-xs font-bold ${badgeTheme}`}>
          {decision}
        </span>
      </div>

      {/* Remarks or Rejection Reason */}
      {isRejected ? (
        <div className="mt-3 rounded-lg border border-red-200 bg-white p-3 shadow-xs">
          <p className="text-[11px] font-bold uppercase tracking-wide text-red-600">
            Rejection Reason
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm font-medium text-red-800">
            {remarks || "No rejection reason provided."}
          </p>
        </div>
      ) : remarks ? (
        <div className="mt-3 rounded-lg border border-emerald-100 bg-white/90 p-3 shadow-xs">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Approval Remarks
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
            {remarks}
          </p>
        </div>
      ) : null}

      {/* Rejection Photographs Evidence */}
      {isRejected && photosList.length > 0 && (
        <div className="mt-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-red-600">
            Attached Rejection Photograph(s)
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {photosList.map((photo, idx) => {
              const photoUrl =
                photo?.annotated_image_url ||
                photo?.annotated_image ||
                photo?.image_url ||
                photo?.image ||
                null;
              const resolvedSrc = resolveMediaUrl(photoUrl);

              return (
                <div
                  key={photo?.id || idx}
                  className="rounded-lg border border-red-200 bg-white p-2.5 shadow-xs"
                >
                  <div className="relative mb-2 h-36 w-full overflow-hidden rounded bg-slate-100">
                    {resolvedSrc ? (
                      <img
                        src={resolvedSrc}
                        alt={`Rejection Evidence ${idx + 1}`}
                        className="h-full w-full cursor-zoom-in object-contain transition hover:opacity-90"
                        onClick={() => {
                          if (onImageClick) {
                            onImageClick(
                              resolvedSrc,
                              `Rejection Photograph #${idx + 1}`,
                            );
                          }
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs italic text-slate-400">
                        Image unavailable
                      </div>
                    )}
                    <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      #{idx + 1}
                    </span>
                  </div>

                  {photo?.comment && (
                    <div className="rounded border border-red-100 bg-red-50/50 p-2 text-xs text-red-900">
                      <strong>Comment:</strong> {photo.comment}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Signature Section */}
      {signature && (
        <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Signature
            </p>
            <p className="text-xs text-slate-600">
              {reviewerName ? `${reviewerName} (${reviewerRole})` : reviewerRole}
            </p>
          </div>
          <div className="rounded border border-slate-200 bg-white p-1 shadow-xs">
            <img
              src={resolveMediaUrl(signature)}
              alt={`${reviewerRole} Signature`}
              className="max-h-12 max-w-[140px] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
